import { useBlockProps, InspectorControls, BlockControls } from "@wordpress/block-editor";
import { PanelBody, SelectControl, TextControl, TextareaControl, Button, ButtonGroup, ToggleControl, Disabled, ToolbarGroup, ToolbarDropdownMenu } from "@wordpress/components";
import { useSelect } from "@wordpress/data";
import DebouncedServerSideRender from "../../../shared/js/DebouncedServerSideRender";
import { __, sprintf } from "@wordpress/i18n";
import { isDuplicateTocAnchor } from "../../../shared/js/tocAnchorDiagnostics";

const VARIANT_OPTIONS = [
    { label: __("Default", "content-egg"), value: "default" },
    { label: __("Accent", "content-egg"), value: "accent" },
    { label: __("Editorial", "content-egg"), value: "editorial" },
];

const HEADING_TAG_OPTIONS = ["h1", "h2", "h3", "h4", "div"];

const COLOR_SCHEME_OPTIONS = [
    { label: __("Auto (site default)", "content-egg"), value: "auto" },
    { label: __("Light", "content-egg"), value: "light" },
    { label: __("Dark", "content-egg"), value: "dark" },
];

export default function Edit({ attributes, setAttributes }) {
    const blockProps = useBlockProps({ className: attributes.align ? `align${attributes.align}` : "" });
    const { variant, step_number, title, heading_tag, subtitle, step_label_text, kicker, meta, chips = [], anchor, toc_label, include_in_toc, level, color_scheme } = attributes;
    const allBlocks = useSelect((select) => select("core/block-editor").getBlocks(), []);
    const hasDuplicateTocAnchor = !!include_in_toc && isDuplicateTocAnchor(allBlocks, anchor);
    const suggestedTocLabel = (title || "").trim();

    const updateChips = (nextChips) => setAttributes({ chips: nextChips });
    const addChip = () => updateChips([...(chips || []), ""]);
    const updateChip = (index, value) => {
        const next = [...(chips || [])];
        next[index] = value;
        updateChips(next);
    };
    const removeChip = (index) => {
        const next = [...(chips || [])];
        next.splice(index, 1);
        updateChips(next);
    };

    const hasPreviewContent =
        (title || "").trim() !== "" ||
        (subtitle || "").trim() !== "" ||
        (kicker || "").trim() !== "" ||
        (meta || "").trim() !== "" ||
        (step_number || "").trim() !== "" ||
        (chips || []).some((chip) => (chip || "").trim() !== "");

    return (
        <>
            <BlockControls>
                <ToolbarGroup>
                    <ToolbarDropdownMenu
                        icon="screenoptions"
                        label={__("Variant", "content-egg")}
                        text={VARIANT_OPTIONS.find((o) => o.value === variant)?.label}
                        controls={VARIANT_OPTIONS.map((option) => ({
                            title: option.label,
                            isActive: variant === option.value,
                            onClick: () => setAttributes({ variant: option.value }),
                        }))}
                    />
                </ToolbarGroup>
            </BlockControls>

            <InspectorControls>
                <PanelBody title={__("Layout", "content-egg")} initialOpen={true}>
                    <SelectControl
                        label={__("Variant", "content-egg")}
                        value={variant}
                        options={VARIANT_OPTIONS}
                        onChange={(value) => setAttributes({ variant: value })}
                        __nextHasNoMarginBottom
                    />
                    <SelectControl
                        label={__("Color Scheme", "content-egg")}
                        value={color_scheme}
                        options={COLOR_SCHEME_OPTIONS}
                        onChange={(value) => setAttributes({ color_scheme: value })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>

                <PanelBody title={__("Content", "content-egg")} initialOpen={true}>
                    <TextControl
                        label={__("Title", "content-egg")}
                        value={title}
                        onChange={(value) => setAttributes({ title: value })}
                    />
                    <div style={{ marginBottom: "8px" }}>
                        <div style={{ fontSize: "11px", fontWeight: 500, marginBottom: "6px" }}>
                            {__("Heading Tag", "content-egg")}
                        </div>
                        <ButtonGroup>
                            {HEADING_TAG_OPTIONS.map((tag) => (
                                <Button
                                    key={tag}
                                    variant={heading_tag === tag ? "primary" : "secondary"}
                                    size="small"
                                    onClick={() => setAttributes({ heading_tag: tag })}
                                >
                                    {tag.toUpperCase()}
                                </Button>
                            ))}
                        </ButtonGroup>
                    </div>
                    <TextareaControl
                        label={__("Subtitle", "content-egg")}
                        value={subtitle}
                        onChange={(value) => setAttributes({ subtitle: value })}
                        rows={3}
                    />
                    <TextControl
                        label={__("Step Number", "content-egg")}
                        type="number"
                        value={step_number || ""}
                        onChange={(value) => setAttributes({ step_number: value })}
                    />
                    <TextControl
                        label={__("Step Label Text", "content-egg")}
                        value={step_label_text}
                        onChange={(value) => setAttributes({ step_label_text: value })}
                    />
                    <TextControl
                        label={__("Kicker", "content-egg")}
                        value={kicker}
                        onChange={(value) => setAttributes({ kicker: value })}
                    />
                    <TextareaControl
                        label={__("Meta", "content-egg")}
                        value={meta}
                        onChange={(value) => setAttributes({ meta: value })}
                        rows={2}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>

                <PanelBody title={__("Chips", "content-egg")} initialOpen={false}>
                    {(chips || []).map((chip, index) => (
                        <div key={index} style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "6px" }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <TextControl
                                    value={chip}
                                    onChange={(value) => updateChip(index, value)}
                                    __nextHasNoMarginBottom
                                />
                            </div>
                            <Button
                                icon="no-alt"
                                isDestructive
                                size="small"
                                onClick={() => removeChip(index)}
                                label={__("Remove", "content-egg")}
                                style={{ flexShrink: 0 }}
                            />
                        </div>
                    ))}
                    <Button variant="secondary" size="small" onClick={addChip} style={{ marginTop: "4px" }}>
                        {__("Add chip", "content-egg")}
                    </Button>
                </PanelBody>

                <PanelBody title={__("TOC", "content-egg")} initialOpen={false}>
                    <TextControl
                        label={__("Anchor", "content-egg")}
                        value={anchor || ""}
                        onChange={(value) => setAttributes({ anchor: value })}
                    />
                    {hasDuplicateTocAnchor && (
                        <p style={{ marginTop: "-4px", marginBottom: "12px", color: "#8a1f11", fontSize: "12px" }}>
                            {__("This anchor is already used by another TOC block.", "content-egg")}
                        </p>
                    )}
                    <ToggleControl
                        label={__("Include In TOC", "content-egg")}
                        checked={!!include_in_toc}
                        onChange={(value) => setAttributes({ include_in_toc: value })}
                    />
                    {include_in_toc && !(anchor || "").trim() && (
                        <p style={{ marginTop: "-4px", marginBottom: "12px", color: "#757575", fontSize: "12px" }}>
                            {__("Set an anchor so the TOC can link to this block.", "content-egg")}
                        </p>
                    )}
                    <TextControl
                        label={__("TOC Label", "content-egg")}
                        value={toc_label || ""}
                        onChange={(value) => setAttributes({ toc_label: value })}
                    />
                    {include_in_toc && !(toc_label || "").trim() && (
                        <p style={{ marginTop: "-4px", marginBottom: "12px", color: "#757575", fontSize: "12px" }}>
                            {suggestedTocLabel
                                ? sprintf(__("Suggested label: %s", "content-egg"), `"${suggestedTocLabel}"`)
                                : __("Set a TOC label so this block can appear in automatic TOC mode.", "content-egg")}
                        </p>
                    )}
                    <TextControl
                        label={__("Level", "content-egg")}
                        type="number"
                        min={1}
                        max={6}
                        value={level ?? 2}
                        onChange={(value) => setAttributes({ level: value === "" ? 2 : Number(value) })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>
            </InspectorControls>

            <div {...blockProps}>
                {hasPreviewContent ? (
                    <Disabled>
                        <DebouncedServerSideRender
                            block="eggb/section-header"
                            attributes={attributes}
                        />
                    </Disabled>
                ) : (
                    <div
                        style={{
                            minHeight: "120px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "20px",
                            border: "1px dashed #c3c4c7",
                            borderRadius: "6px",
                            background: "#f6f7f7",
                            textAlign: "center",
                        }}
                    >
                        <p style={{ color: "#757575", fontStyle: "italic", margin: 0, fontSize: "13px" }}>
                            {__("Add section header content in the sidebar.", "content-egg")}
                        </p>
                    </div>
                )}
            </div>
        </>
    );
}
